import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Stage, Layer, Rect as KRect, Ellipse as KEllipse, Line } from 'react-konva';
import type Konva from 'konva';
import * as Y from 'yjs';
import type { P2pProvider } from '../sync/p2pProvider';
import { ShapeRenderer } from './ShapeRenderer';
import { useShapes } from './binding/useShapes';
import {
  addShape,
  deleteShapes,
  expandFramesToChildren,
  expandSelectionToGroups,
  moveShapesBy,
  rectsIntersect,
  getShapesMap,
  resizeShape,
  shapeBBox,
  snapshotPositions,
} from './binding/shapesMap';
import {
  isTextEditable,
  makeArrow,
  makeEllipse,
  makeFrame,
  makeFreehand,
  makeRect,
  makeSticky,
  makeText,
  MIN_SHAPE_SIZE,
  WIDE_STICKY_H,
  WIDE_STICKY_W,
  type ToolId,
  type WorldPoint,
} from './tools';
import { useTokens } from '../styles/theme';
import { RemoteCursors } from '../awareness/RemoteCursors';
import type { AwarenessState } from '../awareness/types';
import type { Shape } from '@frame/shared';
import { TextEditor } from './TextEditor';
import { findImageFile, importImage } from './imageImport';
import { SelectionHandles } from './SelectionHandles';
import { computeResize, cursorForHandle, type ResizeHandle } from './resize';
import { TextFormatToolbar } from './TextFormatToolbar';
import { isTextBearing } from './textStyle';
import { MIN_SCALE, MAX_SCALE, type Viewport, type Size } from './viewport';
import { computeSnap, type GuideSpan } from './alignmentGuides';
import { pickHandle, pickTopShape } from './picking';
import {
  readStickyColor,
  readStickySize,
  writeStickySize,
} from './stickyPrefs';

interface CanvasProps {
  doc: Y.Doc;
  provider: P2pProvider;
  peers: Map<number, AwarenessState>;
  peerSelectionColors: Map<string, { color: string }[]>;
  selectedIds: Set<string>;
  setSelectedIds: (ids: Set<string>) => void;
  activeTool: ToolId;
  setActiveTool: (tool: ToolId) => void;
  authorId: string;
  viewport: Viewport;
  setViewport: (next: Viewport | ((prev: Viewport) => Viewport)) => void;
  onSizeChange?: (size: Size) => void;
  onContextMenu?: (info: { screenX: number; screenY: number; shapeId: string | null }) => void;
  // Override the shapes rendered on the canvas. When provided, replaces the
  // internal useShapes(doc) read — used by retro's anonymous write phase to
  // render other-author text as placeholders. Interaction (picking, drag,
  // bbox) operates on the overridden shapes, which is intentional.
  shapesOverride?: readonly Shape[];
  // Fires once on mount so the parent (BoardPage) can hold a reference to the
  // live Konva Stage — used by the exporters which re-transform the stage to
  // snapshot the full board bbox.
  onStageReady?: (stage: Konva.Stage) => void;
}

interface DragState {
  startWorld: WorldPoint;
  originals: Map<string, { x: number; y: number }>;
}

interface LassoState {
  startWorld: WorldPoint;
  endWorld: WorldPoint;
  additive: boolean;
}

interface CreateState {
  tool: 'frame' | 'rect' | 'ellipse' | 'arrow' | 'pen';
  startWorld: WorldPoint;
  endWorld: WorldPoint;
  penPoints?: number[];
}

interface ResizeState {
  shapeId: string;
  handle: ResizeHandle;
  startWorld: WorldPoint;
  original: { x: number; y: number; w: number; h: number };
}

const ZOOM_FACTOR = 1.05;
const CURSOR_THROTTLE_MS = 33;

// ─────────────────────────────────────────────────────────────────────────
// Interaction model
//
// The two Konva layers render with `listening={false}` — they are pure
// pixels. Every pointer gesture is handled at the Stage level: we read the
// raw pointer, convert it to world space, and resolve what was hit in plain
// JS against the live shape list (see picking.ts). Nothing depends on Konva's
// internal hit canvas, which historically drifted out of sync with the scene
// in production builds (pan/zoom moved shapes on screen while their clickable
// regions stayed put). This is deterministic and always matches what's drawn.
// ─────────────────────────────────────────────────────────────────────────

// Project each dragging shape's *current* bbox (start + delta) for snap math.
function draggingBBoxes(
  shapes: readonly Shape[],
  originals: Map<string, { x: number; y: number }>,
  dx: number,
  dy: number,
): { x: number; y: number; w: number; h: number }[] {
  const byId = new Map<string, Shape>();
  for (const s of shapes) byId.set(s.id, s);
  const out: { x: number; y: number; w: number; h: number }[] = [];
  for (const [id, orig] of originals) {
    const shape = byId.get(id);
    if (!shape) continue;
    if ('w' in shape && 'h' in shape) {
      out.push({ x: orig.x + dx, y: orig.y + dy, w: shape.w, h: shape.h });
    }
  }
  return out;
}

// Everything not currently being dragged becomes a snap target.
function nonDraggingBBoxes(
  shapes: readonly Shape[],
  originals: Map<string, { x: number; y: number }>,
): { x: number; y: number; w: number; h: number }[] {
  const out: { x: number; y: number; w: number; h: number }[] = [];
  for (const s of shapes) {
    if (originals.has(s.id)) continue;
    if ('w' in s && 'h' in s) out.push({ x: s.x, y: s.y, w: s.w, h: s.h });
  }
  return out;
}

export function Canvas({
  doc,
  provider,
  peers,
  peerSelectionColors,
  selectedIds,
  setSelectedIds,
  activeTool,
  setActiveTool,
  authorId,
  viewport,
  setViewport,
  onSizeChange,
  onContextMenu,
  shapesOverride,
  onStageReady,
}: CanvasProps) {
  const tokens = useTokens();
  const containerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<Konva.Stage>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  const [pan, setPan] = useState(false);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [lasso, setLasso] = useState<LassoState | null>(null);
  const [create, setCreate] = useState<CreateState | null>(null);
  const [resize, setResize] = useState<ResizeState | null>(null);
  const [handleHoverCursor, setHandleHoverCursor] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [snapGuides, setSnapGuides] = useState<{ v: GuideSpan[]; h: GuideSpan[] }>({ v: [], h: [] });
  const spaceHeldRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastWorldPtRef = useRef<WorldPoint>({ x: 0, y: 0 });

  const docShapes = useShapes(doc);
  const shapes = shapesOverride ?? docShapes;

  useEffect(() => {
    if (onStageReady && stageRef.current) onStageReady(stageRef.current);
  }, [onStageReady]);

  const shapesById = useMemo(() => {
    const m = new Map<string, (typeof shapes)[number]>();
    for (const s of shapes) m.set(s.id, s);
    return m;
  }, [shapes]);
  const editingShape = editingId ? shapesById.get(editingId) ?? null : null;

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const next = { width: el.clientWidth, height: el.clientHeight };
      setSize(next);
      onSizeChange?.(next);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [onSizeChange]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !e.repeat) {
        spaceHeldRef.current = true;
        document.body.style.cursor = 'grab';
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        spaceHeldRef.current = false;
        document.body.style.cursor = '';
      }
    };
    const clearCursor = () => provider.awareness.setLocalStateField('cursor', null);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', clearCursor);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', clearCursor);
      document.body.style.cursor = '';
    };
  }, [provider.awareness]);

  useEffect(() => {
    provider.awareness.setLocalStateField('activeTool', activeTool);
  }, [activeTool, provider.awareness]);

  const cursorRef = useRef<WorldPoint | null>(null);
  const cursorTimerRef = useRef<number | null>(null);
  const flushCursor = useCallback(() => {
    cursorTimerRef.current = null;
    provider.awareness.setLocalStateField('cursor', cursorRef.current);
  }, [provider.awareness]);
  const queueCursorBroadcast = useCallback(() => {
    if (cursorTimerRef.current != null) return;
    cursorTimerRef.current = window.setTimeout(flushCursor, CURSOR_THROTTLE_MS);
  }, [flushCursor]);

  const toWorld = useCallback(
    (stagePt: WorldPoint) => ({
      x: (stagePt.x - viewport.x) / viewport.scale,
      y: (stagePt.y - viewport.y) / viewport.scale,
    }),
    [viewport.x, viewport.y, viewport.scale],
  );

  // Image tool is one-shot: opening the picker IS the interaction.
  useEffect(() => {
    if (activeTool !== 'image') return;
    const input = fileInputRef.current;
    if (!input) return;
    const onCancel = () => setActiveTool('select');
    input.addEventListener('cancel', onCancel);
    input.click();
    return () => input.removeEventListener('cancel', onCancel);
  }, [activeTool, setActiveTool]);

  const handlePickedImage = useCallback(
    async (file: File | null) => {
      setActiveTool('select');
      if (!file) return;
      const center = viewportCenter(size, viewport);
      await importImage({ doc, file, at: center, authorId });
    },
    [doc, authorId, setActiveTool, size, viewport],
  );

  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && isFormField(target)) return;
      const file = findImageFile(e.clipboardData?.items);
      if (!file) return;
      e.preventDefault();
      void importImage({ doc, file, at: lastWorldPtRef.current, authorId });
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [doc, authorId]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onDragOver = (e: DragEvent) => {
      if (!e.dataTransfer) return;
      if (!Array.from(e.dataTransfer.items ?? []).some((i) => i.kind === 'file')) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    };
    const onDrop = (e: DragEvent) => {
      const file = findImageFile(e.dataTransfer?.items);
      if (!file) return;
      e.preventDefault();
      const stage = stageRef.current;
      const pointer = stage?.getPointerPosition() ?? null;
      const at = pointer ? toWorld(pointer) : lastWorldPtRef.current;
      void importImage({ doc, file, at, authorId });
    };
    el.addEventListener('dragover', onDragOver);
    el.addEventListener('drop', onDrop);
    return () => {
      el.removeEventListener('dragover', onDragOver);
      el.removeEventListener('drop', onDrop);
    };
  }, [doc, authorId, toWorld]);

  const cancelInteractions = useCallback(() => {
    setCreate(null);
    setLasso(null);
    setDrag(null);
    setResize(null);
    setHandleHoverCursor(null);
  }, []);

  useEffect(() => {
    if (selectedIds.size !== 1) setHandleHoverCursor(null);
  }, [selectedIds]);

  // Esc commits an open edit, otherwise resets to the select tool and clears
  // any in-progress gesture. Committing here means edit mode can never trap
  // the pointer even if the textarea lost focus without firing blur.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (editingId) {
        setEditingId(null);
        return;
      }
      setActiveTool('select');
      cancelInteractions();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editingId, setActiveTool, cancelInteractions]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Backspace' && e.key !== 'Delete') return;
      if (editingId) return;
      const target = e.target as HTMLElement | null;
      if (target && isFormField(target)) return;
      if (selectedIds.size === 0) return;
      e.preventDefault();
      deleteShapes(doc, selectedIds);
      setSelectedIds(new Set());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editingId, selectedIds, doc, setSelectedIds]);

  const handleWheel = (e: Konva.KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault();
    const stage = stageRef.current;
    if (!stage) return;

    const isZoom = e.evt.ctrlKey || e.evt.metaKey;
    if (!isZoom) {
      setViewport((v) => ({ ...v, x: v.x - e.evt.deltaX, y: v.y - e.evt.deltaY }));
      return;
    }

    const pointer = stage.getPointerPosition();
    if (!pointer) return;
    const oldScale = viewport.scale;
    const direction = e.evt.deltaY > 0 ? -1 : 1;
    const raw = direction > 0 ? oldScale * ZOOM_FACTOR : oldScale / ZOOM_FACTOR;
    const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, raw));

    const worldX = (pointer.x - viewport.x) / oldScale;
    const worldY = (pointer.y - viewport.y) / oldScale;
    setViewport({
      x: pointer.x - worldX * newScale,
      y: pointer.y - worldY * newScale,
      scale: newScale,
    });
  };

  // True when the solo-selected shape currently shows resize handles (select
  // tool, not editing, unlocked, has w/h). Used to gate handle picking so it
  // matches what's drawn.
  const resizableSolo = useMemo(() => {
    if (activeTool !== 'select' || editingId || selectedIds.size !== 1) return null;
    const id = [...selectedIds][0]!;
    const s = shapesById.get(id);
    if (!s || s.locked || !('w' in s) || !('h' in s)) return null;
    const bbox = shapeBBox(s);
    if (!bbox) return null;
    return { id, shape: s, bbox, layout: (s.type === 'sticky' ? 'sticky' : 'all') as 'all' | 'sticky' };
  }, [activeTool, editingId, selectedIds, shapesById]);

  const handleMouseDown = (e: Konva.KonvaEventObject<MouseEvent>) => {
    const stage = stageRef.current;
    if (!stage) return;
    // Commit any open text edit before handling a fresh gesture. A stage
    // press means the click landed outside the editing textarea (a DOM
    // sibling), so finishing the edit is correct and keeps edit mode from
    // trapping clicks.
    if (editingId) setEditingId(null);
    const pointer = stage.getPointerPosition();
    if (!pointer) return;

    const isMiddle = e.evt.button === 1;
    const isSpacePan = e.evt.button === 0 && spaceHeldRef.current;
    const isHandPan = e.evt.button === 0 && activeTool === 'hand';
    if (isMiddle || isSpacePan || isHandPan) {
      e.evt.preventDefault();
      setPan(true);
      document.body.style.cursor = 'grabbing';
      return;
    }
    if (e.evt.button !== 0) return;
    if (activeTool === 'image') return;

    const worldPt = toWorld(pointer);

    // Resize handles win over everything else when visible.
    if (resizableSolo) {
      const handle = pickHandle(resizableSolo.bbox, worldPt, viewport.scale, resizableSolo.layout);
      if (handle) {
        const s = resizableSolo.shape as Shape & { x: number; y: number; w: number; h: number };
        setResize({
          shapeId: resizableSolo.id,
          handle,
          startWorld: worldPt,
          original: { x: s.x, y: s.y, w: s.w, h: s.h },
        });
        return;
      }
    }

    // Click-create tools — sticky / text.
    if (activeTool === 'sticky' || activeTool === 'sticky-wide') {
      const color = readStickyColor() ?? 'sand';
      const stickySize =
        activeTool === 'sticky-wide'
          ? { w: WIDE_STICKY_W, h: WIDE_STICKY_H }
          : readStickySize();
      const sticky = makeSticky(worldPt, { authorId }, stickySize, color);
      addShape(doc, sticky);
      setSelectedIds(new Set([sticky.id]));
      setEditingId(sticky.id);
      setActiveTool('select');
      return;
    }
    if (activeTool === 'text') {
      const t = makeText(worldPt, { authorId });
      addShape(doc, t);
      setSelectedIds(new Set([t.id]));
      setEditingId(t.id);
      setActiveTool('select');
      return;
    }

    // Drag-create tools — frame / rect / ellipse / arrow / pen.
    if (
      activeTool === 'frame' ||
      activeTool === 'rect' ||
      activeTool === 'ellipse' ||
      activeTool === 'arrow' ||
      activeTool === 'pen'
    ) {
      setSelectedIds(new Set());
      setCreate({
        tool: activeTool,
        startWorld: worldPt,
        endWorld: worldPt,
        penPoints: activeTool === 'pen' ? [worldPt.x, worldPt.y] : undefined,
      });
      return;
    }

    // Select tool: pick the topmost shape under the cursor → drag; empty → lasso.
    const shapeId = pickTopShape(shapes, worldPt, viewport.scale);
    if (shapeId) {
      let nextSelected = selectedIds;
      if (e.evt.shiftKey) {
        nextSelected = new Set(selectedIds);
        if (nextSelected.has(shapeId)) nextSelected.delete(shapeId);
        else nextSelected.add(shapeId);
      } else if (!selectedIds.has(shapeId)) {
        nextSelected = new Set([shapeId]);
      }
      if (!e.evt.shiftKey) {
        nextSelected = expandSelectionToGroups(nextSelected, shapes);
      }
      setSelectedIds(nextSelected);

      const allLocked = [...nextSelected].every((id) => shapesById.get(id)?.locked);
      if (allLocked) return;

      const dragIds = nextSelected.size > 0 ? nextSelected : new Set([shapeId]);
      const expandedIds = expandFramesToChildren(dragIds, shapes);
      const movableIds = new Set<string>();
      for (const id of expandedIds) {
        if (!shapesById.get(id)?.locked) movableIds.add(id);
      }
      if (movableIds.size === 0) return;
      setDrag({
        startWorld: worldPt,
        originals: snapshotPositions(doc, movableIds),
      });
      return;
    }

    setLasso({
      startWorld: worldPt,
      endWorld: worldPt,
      additive: e.evt.shiftKey,
    });
    if (!e.evt.shiftKey) setSelectedIds(new Set());
  };

  const handleMouseMove = (e: Konva.KonvaEventObject<MouseEvent>) => {
    const stage = stageRef.current;
    if (!stage) return;
    const pointer = stage.getPointerPosition();
    if (!pointer) return;
    const worldPt = toWorld(pointer);

    cursorRef.current = worldPt;
    lastWorldPtRef.current = worldPt;
    queueCursorBroadcast();

    if (pan) {
      setViewport((v) => ({
        ...v,
        x: v.x + e.evt.movementX,
        y: v.y + e.evt.movementY,
      }));
      return;
    }
    if (resize) {
      const target = shapesById.get(resize.shapeId);
      const isSticky = target?.type === 'sticky';
      const handleIsCorner =
        resize.handle === 'nw' ||
        resize.handle === 'ne' ||
        resize.handle === 'se' ||
        resize.handle === 'sw';
      const stickyForcesAspect = isSticky && handleIsCorner;
      const box = computeResize({
        handle: resize.handle,
        original: resize.original,
        delta: { x: worldPt.x - resize.startWorld.x, y: worldPt.y - resize.startWorld.y },
        aspectLock: stickyForcesAspect || e.evt.shiftKey,
        fromCenter: e.evt.altKey,
        minSize: MIN_SHAPE_SIZE,
      });
      resizeShape(doc, resize.shapeId, box);
      return;
    }
    if (drag) {
      const dx = worldPt.x - drag.startWorld.x;
      const dy = worldPt.y - drag.startWorld.y;
      const skipSnap = e.evt.shiftKey;
      const movingBoxes = skipSnap ? [] : draggingBBoxes(shapes, drag.originals, dx, dy);
      const otherBoxes = skipSnap ? [] : nonDraggingBBoxes(shapes, drag.originals);
      const snap = computeSnap(movingBoxes, otherBoxes, 6, viewport.scale);
      moveShapesBy(doc, drag.originals, dx + snap.dx, dy + snap.dy);
      setSnapGuides({ v: snap.vGuides, h: snap.hGuides });
      return;
    }
    if (create) {
      if (create.tool === 'pen') {
        setCreate({
          ...create,
          endWorld: worldPt,
          penPoints: [...(create.penPoints ?? []), worldPt.x, worldPt.y],
        });
      } else {
        setCreate({ ...create, endWorld: worldPt });
      }
      return;
    }
    if (lasso) {
      setLasso({ ...lasso, endWorld: worldPt });
      return;
    }

    // Idle: show the resize cursor when hovering a handle of the solo
    // selection. setState bails out when the value is unchanged, so this is
    // cheap to run on every move.
    if (resizableSolo) {
      const handle = pickHandle(resizableSolo.bbox, worldPt, viewport.scale, resizableSolo.layout);
      setHandleHoverCursor(handle ? cursorForHandle(handle) : null);
    } else {
      setHandleHoverCursor(null);
    }
  };

  const commitCreate = useCallback(
    (c: CreateState) => {
      const ctx = { authorId };
      switch (c.tool) {
        case 'frame': {
          const f = makeFrame(c.startWorld, c.endWorld, ctx);
          addShape(doc, f);
          setSelectedIds(new Set([f.id]));
          return;
        }
        case 'rect': {
          const r = makeRect(c.startWorld, c.endWorld, ctx);
          addShape(doc, r);
          setSelectedIds(new Set([r.id]));
          return;
        }
        case 'ellipse': {
          const el = makeEllipse(c.startWorld, c.endWorld, ctx);
          addShape(doc, el);
          setSelectedIds(new Set([el.id]));
          return;
        }
        case 'arrow': {
          const a = makeArrow(c.startWorld, c.endWorld, ctx);
          addShape(doc, a);
          setSelectedIds(new Set([a.id]));
          return;
        }
        case 'pen': {
          const pts = c.penPoints ?? [];
          if (pts.length >= 4) {
            const f = makeFreehand(pts, ctx);
            addShape(doc, f);
            setSelectedIds(new Set([f.id]));
          }
          return;
        }
      }
    },
    [doc, authorId, setSelectedIds],
  );

  const handleMouseUp = () => {
    if (drag) setSnapGuides({ v: [], h: [] });
    if (pan) {
      setPan(false);
      const idleCursor = spaceHeldRef.current || activeTool === 'hand' ? 'grab' : '';
      document.body.style.cursor = idleCursor;
    }
    if (resize) {
      const ymap = getShapesMap(doc).get(resize.shapeId);
      if (ymap?.get('type') === 'sticky') {
        const w = ymap.get('w');
        const h = ymap.get('h');
        if (typeof w === 'number' && typeof h === 'number') {
          writeStickySize({ w, h });
        }
      }
      setResize(null);
    }
    if (drag) setDrag(null);
    if (create) {
      commitCreate(create);
      setCreate(null);
      setActiveTool('select');
    }
    if (lasso) {
      const x = Math.min(lasso.startWorld.x, lasso.endWorld.x);
      const y = Math.min(lasso.startWorld.y, lasso.endWorld.y);
      const w = Math.abs(lasso.endWorld.x - lasso.startWorld.x);
      const h = Math.abs(lasso.endWorld.y - lasso.startWorld.y);
      if (w > 3 || h > 3) {
        const lassoBox = { x, y, w, h };
        const hit = new Set(lasso.additive ? selectedIds : []);
        for (const shape of shapes) {
          const bbox = shapeBBox(shape);
          if (!bbox) continue;
          if (rectsIntersect(lassoBox, bbox)) hit.add(shape.id);
        }
        setSelectedIds(expandSelectionToGroups(hit, shapes));
      }
      setLasso(null);
    }
  };

  const handleMouseLeave = () => {
    handleMouseUp();
    cursorRef.current = null;
    if (cursorTimerRef.current != null) {
      window.clearTimeout(cursorTimerRef.current);
      cursorTimerRef.current = null;
    }
    provider.awareness.setLocalStateField('cursor', null);
  };

  const handleDblClick = () => {
    if (activeTool !== 'select') return;
    const stage = stageRef.current;
    const pointer = stage?.getPointerPosition();
    if (!pointer) return;
    const shapeId = pickTopShape(shapes, toWorld(pointer), viewport.scale);
    if (!shapeId) return;
    const shape = shapesById.get(shapeId);
    if (!shape || !isTextEditable(shape) || shape.locked) return;
    setSelectedIds(new Set([shapeId]));
    setEditingId(shapeId);
  };

  const handleContextMenu = (e: Konva.KonvaEventObject<PointerEvent>) => {
    if (!onContextMenu) return;
    e.evt.preventDefault();
    const stage = stageRef.current;
    const pointer = stage?.getPointerPosition();
    const shapeId = pointer ? pickTopShape(shapes, toWorld(pointer), viewport.scale) : null;
    if (shapeId && !selectedIds.has(shapeId)) {
      setSelectedIds(new Set([shapeId]));
    }
    onContextMenu({
      screenX: e.evt.clientX,
      screenY: e.evt.clientY,
      shapeId,
    });
  };

  const soloSelectedShape =
    selectedIds.size === 1 ? shapesById.get([...selectedIds][0]!) ?? null : null;

  const handlesShape =
    activeTool === 'select' &&
    !editingId &&
    soloSelectedShape &&
    !soloSelectedShape.locked &&
    'w' in soloSelectedShape &&
    'h' in soloSelectedShape
      ? soloSelectedShape
      : null;
  const handlesBBox = handlesShape ? shapeBBox(handlesShape) : null;
  const handlesLayout: 'all' | 'sticky' = handlesShape?.type === 'sticky' ? 'sticky' : 'all';

  const formatShape = editingShape && isTextBearing(editingShape)
    ? editingShape
    : soloSelectedShape && isTextBearing(soloSelectedShape)
      ? soloSelectedShape
      : null;

  const baseCursor = cursorForTool(activeTool, pan, spaceHeldRef.current);
  const cursorStyle = resize
    ? cursorForHandle(resize.handle)
    : handleHoverCursor ?? baseCursor;

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height: '100%', cursor: cursorStyle, position: 'relative' }}
    >
      <Stage
        ref={stageRef}
        width={size.width}
        height={size.height}
        x={viewport.x}
        y={viewport.y}
        scaleX={viewport.scale}
        scaleY={viewport.scale}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onDblClick={handleDblClick}
        onContextMenu={handleContextMenu}
      >
        {/* Render-only: all hit-testing happens in JS at the Stage level. */}
        <Layer listening={false}>
          {shapes.map((shape) => (
            <ShapeRenderer
              key={shape.id}
              shape={shape}
              selected={selectedIds.has(shape.id)}
              peerSelections={peerSelectionColors.get(shape.id)}
              editing={shape.id === editingId}
            />
          ))}
          {handlesBBox && (
            <SelectionHandles
              bbox={handlesBBox}
              scale={viewport.scale}
              tokens={tokens}
              onHover={noop}
              layout={handlesLayout}
            />
          )}
          {lasso && <LassoRect lasso={lasso} tokens={tokens} />}
          {create && <CreatePreview create={create} tokens={tokens} />}
        </Layer>
        <Layer listening={false}>
          <RemoteCursors peers={peers} tokens={tokens} scale={viewport.scale} />
          <AlignmentGuides
            v={snapGuides.v}
            h={snapGuides.h}
            color={tokens.accent}
            scale={viewport.scale}
          />
        </Layer>
      </Stage>
      {editingShape && (
        <TextEditor
          doc={doc}
          shape={editingShape}
          viewport={viewport}
          onDone={() => setEditingId(null)}
        />
      )}
      {formatShape && (
        <TextFormatToolbar
          doc={doc}
          shape={formatShape}
          viewport={viewport}
          containerSize={size}
          editing={!!editingId}
        />
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0] ?? null;
          e.target.value = '';
          void handlePickedImage(file);
        }}
      />
    </div>
  );
}

function noop() {}

// Thin accent-colored lines drawn while a snap is active.
function AlignmentGuides({
  v,
  h,
  color,
  scale,
}: {
  v: GuideSpan[];
  h: GuideSpan[];
  color: string;
  scale: number;
}) {
  if (v.length === 0 && h.length === 0) return null;
  const w = 1 / scale;
  return (
    <>
      {v.map((g, i) => (
        <Line key={`v-${i}`} points={[g.pos, g.start, g.pos, g.end]} stroke={color} strokeWidth={w} />
      ))}
      {h.map((g, i) => (
        <Line key={`h-${i}`} points={[g.start, g.pos, g.end, g.pos]} stroke={color} strokeWidth={w} />
      ))}
    </>
  );
}

function viewportCenter(
  size: { width: number; height: number },
  viewport: Viewport,
): WorldPoint {
  return {
    x: (size.width / 2 - viewport.x) / viewport.scale,
    y: (size.height / 2 - viewport.y) / viewport.scale,
  };
}

function LassoRect({
  lasso,
  tokens,
}: {
  lasso: LassoState;
  tokens: ReturnType<typeof useTokens>;
}) {
  const x = Math.min(lasso.startWorld.x, lasso.endWorld.x);
  const y = Math.min(lasso.startWorld.y, lasso.endWorld.y);
  const w = Math.abs(lasso.endWorld.x - lasso.startWorld.x);
  const h = Math.abs(lasso.endWorld.y - lasso.startWorld.y);
  return (
    <KRect
      x={x}
      y={y}
      width={w}
      height={h}
      fill={tokens.lassoFill}
      stroke={tokens.lassoStroke}
      strokeWidth={1}
      dash={[3, 3]}
      listening={false}
    />
  );
}

// Ghost preview during a create-drag.
function CreatePreview({
  create,
  tokens,
}: {
  create: CreateState;
  tokens: ReturnType<typeof useTokens>;
}) {
  const x = Math.min(create.startWorld.x, create.endWorld.x);
  const y = Math.min(create.startWorld.y, create.endWorld.y);
  const w = Math.abs(create.endWorld.x - create.startWorld.x);
  const h = Math.abs(create.endWorld.y - create.startWorld.y);

  if (create.tool === 'frame' || create.tool === 'rect') {
    return (
      <KRect
        x={x}
        y={y}
        width={w}
        height={h}
        fill="transparent"
        stroke={tokens.accent}
        strokeWidth={1}
        dash={[4, 4]}
        listening={false}
      />
    );
  }
  if (create.tool === 'ellipse') {
    return (
      <KEllipse
        x={x + w / 2}
        y={y + h / 2}
        radiusX={w / 2}
        radiusY={h / 2}
        fill="transparent"
        stroke={tokens.accent}
        strokeWidth={1}
        dash={[4, 4]}
        listening={false}
      />
    );
  }
  if (create.tool === 'arrow') {
    return (
      <Line
        points={[create.startWorld.x, create.startWorld.y, create.endWorld.x, create.endWorld.y]}
        stroke={tokens.accent}
        strokeWidth={1.5}
        dash={[4, 4]}
        lineCap="round"
        listening={false}
      />
    );
  }
  if (create.tool === 'pen') {
    return (
      <Line
        points={create.penPoints ?? []}
        stroke={tokens.fgPrimary}
        strokeWidth={2}
        lineCap="round"
        lineJoin="round"
        tension={0.4}
        listening={false}
      />
    );
  }
  return null;
}

function cursorForTool(tool: ToolId, pan: boolean, spaceHeld: boolean): string {
  if (pan) return 'grabbing';
  if (spaceHeld || tool === 'hand') return 'grab';
  if (tool === 'select') return 'default';
  return 'crosshair';
}

function isFormField(el: HTMLElement): boolean {
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'INPUT') return true;
  return el.isContentEditable;
}
